import axios from "axios";

const baseURL = import.meta.env.VITE_API_BASE ?? (import.meta.env.DEV ? "http://localhost:8000" : "");
export const API_BASE_URL = baseURL;

const api = axios.create({
  baseURL
});

export async function fetchSteps() {
  const { data } = await api.get("/steps");
  return data;
}

export async function fetchStepConfig(stepId: string) {
  const { data } = await api.get(`/steps/${stepId}`);
  return data;
}

export type ProjectWorkflowStatus = "termine" | "en_cours" | "brouillon";

export type ProjectSummary = {
  name: string;
  scenario_target: number;
  finalized_at?: string;
  final_audio?: {
    path: string;
    generated_at?: string;
    language?: string;
  } | null;
  final_slideshow?: {
    path: string;
    created_at?: string;
  } | null;
  /** Notes projet (aperçu), aligné page Détails du projet */
  description_preview?: string | null;
  /** ISO — présent quand le projet a été créé via l’API / config */
  created_at?: string;
  /** Fichiers dans `outputs/` du projet */
  artifact_count?: number;
  /** Thèmes issus de `scenario_config.historical_context.themes` si Agent 0 a tourné */
  tags?: string[];
  /** Lieu principal du contexte historique si présent dans la config */
  location?: string | null;
  workflow_status?: ProjectWorkflowStatus;
  /** Réservé — activer côté config quand le backend exposera le K-graph */
  has_k_graph?: boolean;
};

export type PreferenceOptions = {
  tone_options: string[];
  audience_options: string[];
  duration: {
    min: number;
    max: number;
    step: number;
    default: number;
  };
};

export type ProjectProfile = {
  name: string;
  scenario_target: number;
  project_notes?: string;
  voice_instructions?: string;
  voice_instructions_source?: string;
  allowed_websites?: string[];
  audience?: string;
  tone?: string;
  target_duration?: number;
  tts_provider?: "qwen" | "elevenlabs";
  tts_voice_id?: string | null;
  include_citations?: boolean;
  source_usage_level?: "leger" | "modere" | "central";
  preference_options?: PreferenceOptions;
  last_scenarios?: Array<Record<string, unknown>>;
  last_scenarios_generated_at?: string;
  final_scenario?: Record<string, unknown>;
  final_audio?: {
    path: string;
    generated_at?: string;
    language?: string;
    sample_rate?: number;
  };
  final_slideshow?: {
    path: string;
    created_at?: string;
  };
  audio_selection?: AudioSelection;
  project_keywords?: string[];
};

export type BackgroundSelection = {
  ambient?: string | null;
  punctual: string[];
};

export type ClipState = {
  volume?: number;          // ratio 0..1 (1 = base)
  start_seconds?: number;
  duration_seconds?: number;
};

// Instance-based timeline clip. Each clip is independent (unique id), references
// a source file, and plays an arbitrary slice of it (source_offset → +duration),
// which is what makes duplicates independent and the scissors / split possible.
// track_id is optional for backward compatibility — projects predating the
// dynamic-tracks change have no track_id and fall back to the default lane for
// their kind ("ambient" → "default-ambient", "sfx" → "default-sfx").
export type AudioClipInstance = {
  id: string;
  source: string;                  // path of the source sound in the library
  kind: "ambient" | "sfx";
  track_id?: string;               // user-track this clip belongs to
  start_seconds: number;           // position on the timeline
  source_offset_seconds: number;   // where playback starts inside the source
  duration_seconds: number;        // how much of the source to play
  volume: number;                  // 0..1 (1 = base level for its kind)
  fade_in_ms?: number;
  fade_out_ms?: number;
};

// User-defined lane on the timeline. A track groups clips of a single kind and
// carries its own volume/mute/solo state. The defaults "default-ambient" and
// "default-sfx" are created implicitly for projects that have no tracks yet.
export type TrackDefinition = {
  id: string;
  kind: "ambient" | "sfx";
  label: string;
  volume: number;                  // 0..1
  muted?: boolean;
  solo?: boolean;
};

export type AudioSelection = {
  voices: string[];
  backgrounds: BackgroundSelection;
  auto_backgrounds?: boolean;
  tts_voice_id?: string | null;
  tts_provider?: string;
  sfx_slot_positions?: number[];
  narration_volume?: number;
  ambient_volume?: number;
  sfx_volume?: number;
  clip_states?: Record<string, ClipState>;
  clips?: AudioClipInstance[];
  tracks?: TrackDefinition[];
};

export type TranscriptionTopic = {
  title: string;
  summary: string;
  keywords?: string[];
};

export type ProjectTranscription = {
  file_name: string;
  transcription: string;
  summary?: {
    global_summary?: string;
    topics?: TranscriptionTopic[];
  };
  source?: string;
  language?: string;
};

export async function fetchProjects() {
  const { data } = await api.get("/projects");
  return data.projects as ProjectSummary[];
}

export async function fetchProjectProfile(projectName: string) {
  const { data } = await api.get(`/projects/${encodeURIComponent(projectName)}`);
  return data as ProjectProfile;
}

export async function createProject(payload: { name: string; description?: string; scenario_target: number }) {
  const { data } = await api.post("/projects", payload);
  return data;
}

export async function saveVoiceInstructions(projectName: string, voiceInstructions: string) {
  const { data } = await api.post(`/projects/${encodeURIComponent(projectName)}/voice-instructions`, {
    voice_instructions: voiceInstructions,
  });
  return data;
}

export async function fetchProjectKeywords(projectName: string): Promise<string[]> {
  const { data } = await api.get(`/projects/${encodeURIComponent(projectName)}/keywords`);
  return (data.keywords ?? []) as string[];
}

export async function saveProjectKeywords(projectName: string, keywords: string[]): Promise<string[]> {
  const { data } = await api.post(`/projects/${encodeURIComponent(projectName)}/keywords`, { keywords });
  return (data.keywords ?? []) as string[];
}

export type ArtifactSummary = {
  id: string;
  session_id: string;
  name: string;
  status: "en_cours" | "finalise";
  current_step?: string;
  created_at?: string | null;
  has_audio: boolean;
  has_video: boolean;
};

export async function fetchProjectArtifacts(projectName: string) {
  const { data } = await api.get(`/projects/${encodeURIComponent(projectName)}/artifacts`);
  return data.artifacts as ArtifactSummary[];
}

export async function deleteArtifact(projectName: string, sessionId: string) {
  await api.delete(`/projects/${encodeURIComponent(projectName)}/artifacts/${sessionId}`);
}

export function getArtifactAudioUrl(projectName: string, sessionId: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  return `${prefix}/projects/${encodeURIComponent(projectName)}/artifacts/${encodeURIComponent(sessionId)}/audio`;
}

export async function deleteProject(projectName: string) {
  const { data } = await api.delete(`/projects/${encodeURIComponent(projectName)}`);
  return data as { status: string; project: string };
}

export async function renameProject(projectName: string, newName: string) {
  const { data } = await api.post(`/projects/${encodeURIComponent(projectName)}/rename`, {
    new_name: newName
  });
  return data as { status: string; old_name: string; new_name: string; warnings?: string[] };
}

export async function createSession(projectName: string, initialStep = "project_selection", scenarioTarget?: number, forceNew = false) {
  const { data } = await api.post("/sessions", {
    project_name: projectName,
    initial_step: initialStep,
    scenario_target: scenarioTarget,
    force_new: forceNew,
  });
  return data;
}

export async function advanceStep(sessionId: string, stepId: string, payload: Record<string, unknown>) {
  const { data } = await api.post(`/sessions/${sessionId}/step`, {
    step_id: stepId,
    payload
  });
  return data;
}

export async function updateCurrentStep(sessionId: string, stepId: string) {
  const { data } = await api.post(`/sessions/${sessionId}/current-step`, {
    step_id: stepId
  });
  return data;
}

export async function uploadAudio(projectName: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/projects/${projectName}/audio`, form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return data;
}

export type BackgroundSound = {
  path: string;
  name: string;
  preview: string;
  category?: string | null;              // taxonomie utilisateur (null = non classé)
  imported?: boolean;                    // son importé par l'utilisateur (onglet "Mes sons")
  tags?: string[];                       // sémantique (recherche / contenu)
  mix_tags?: string[];                    // caractéristiques de mixage (pastilles)
  suggested_kind?: "ambient" | "sfx" | null;
  description?: string;
  duration?: number;
};

// Fixed, config-driven taxonomy of user-facing sound categories.
export type SoundCategory = { id: string; label: string; color?: string | null };

// Shape returned by POST /background-sounds/upload.
export type UploadedSound = {
  status: string;
  path: string;
  preview: string;
  name?: string;
  category?: string | null;
  imported?: boolean;
  analysis_ok?: boolean;
  mix_tags?: string[];
  suggested_kind?: "ambient" | "sfx" | null;
  duration?: number;
  description?: string;
};

export async function fetchBackgroundSounds(keyword?: string) {
  const { data } = await api.get("/background-sounds", {
    params: keyword ? { keyword } : undefined
  });
  return data.files as BackgroundSound[];
}

export async function fetchSoundCategories() {
  const { data } = await api.get("/background-sounds/categories");
  return (data.categories ?? []) as SoundCategory[];
}

export async function uploadBackgroundSound(title: string, file: File, category?: string | null) {
  const form = new FormData();
  form.append("title", title);
  form.append("file", file);
  if (category) form.append("category", category);
  const { data } = await api.post("/background-sounds/upload", form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return data as UploadedSound;
}

export async function deleteBackgroundSound(path: string) {
  const { data } = await api.delete("/background-sounds", {
    params: { path },
  });
  return data as { status: string; path: string };
}

export type LlmModelInfo = {
  id: string;
  openrouterId: string;
  label: string;
  provider: string;
  description: string;
};

export async function fetchModels(): Promise<LlmModelInfo[]> {
  const { data } = await api.get("/models");
  return data.models as LlmModelInfo[];
}

export type ScenarioSpec = {
  prompt: string;
  audience?: string;
  tone?: string;
  target_duration?: number;
  source_usage_level?: "leger" | "modere" | "central";
  tts_provider?: "elevenlabs" | "qwen";
  tts_voice_id?: string | null;
};

export async function generateScenarios(
  sessionId: string,
  prompt: string,
  scenarioTarget?: number,
  mode: "simple" | "expert" = "simple",
  modelId?: string,
  scenarioSpecs?: ScenarioSpec[],
  force?: boolean
) {
  const { data } = await api.post("/scenarios/generate", {
    session_id: sessionId,
    prompt,
    mode,
    scenario_target: scenarioTarget,
    model_id: modelId || undefined,
    scenario_specs: scenarioSpecs && scenarioSpecs.length > 0 ? scenarioSpecs : undefined,
    force: force || undefined
  });
  return data;
}

export async function fetchProjectAudio(projectName: string) {
  const { data } = await api.get(`/projects/${projectName}/audio`);
  return data.files as string[];
}

export async function fetchVoicePreview(voiceId: string): Promise<Blob> {
  const response = await api.get("/tts/preview", {
    params: { voice_id: voiceId },
    responseType: "blob"
  });
  return response.data as Blob;
}

export async function fetchAudioSelection(sessionId: string) {
  const { data } = await api.get(`/sessions/${sessionId}/audio-selection`);
  return data as AudioSelection;
}

export async function saveAudioSelection(sessionId: string, payload: AudioSelection & { project_name: string }) {
  const { data } = await api.post(`/sessions/${sessionId}/audio-selection`, payload);
  return data as AudioSelection;
}

export async function suggestSfxPositions(sessionId: string) {
  const { data } = await api.post(`/sessions/${sessionId}/suggest-sfx-positions`);
  return data as { positions: number[] };
}

export type KnowledgeGraphEvent = {
  title: string;
  description?: string;
  approximate_time?: string;
  actors?: string[];
  places?: string[];
  keywords?: string[];
};

export type KnowledgeGraph = {
  nodes: { id: string; name: string; type: string; description?: string; time?: string }[];
  edges: { id: string; source: string; target: string; type: string }[];
};

export type KnowledgeGraphBuildStatus = "building" | "ready" | "empty" | "failed";

export type KnowledgeGraphBuild = {
  status: KnowledgeGraphBuildStatus;
  sources_count: number;
  updated_at: string;
  error: string | null;
};

export async function fetchProjectKnowledgeGraph(projectName: string) {
  const { data } = await api.get(`/projects/${encodeURIComponent(projectName)}/knowledge-graph`);
  return data as { events: KnowledgeGraphEvent[]; graph: KnowledgeGraph; build: KnowledgeGraphBuild | null };
}

export async function fetchProjectTranscriptions(projectName: string) {
  const { data } = await api.get(`/projects/${encodeURIComponent(projectName)}/transcriptions`);
  return {
    transcriptions: (data.transcriptions ?? []) as ProjectTranscription[],
    failedFiles: (data.failed_files ?? []) as string[],
    transcriptionErrors: (data.transcription_errors ?? {}) as Record<string, string>,
  };
}

export async function updateProjectTranscription(
  projectName: string,
  payload: { file_name: string; transcription: string }
) {
  const { data } = await api.post(`/projects/${encodeURIComponent(projectName)}/transcriptions`, payload);
  return data as ProjectTranscription;
}

export async function fetchScenarios(sessionId: string) {
  const { data } = await api.get(`/sessions/${sessionId}/scenarios`);
  return data.scenarios as Array<Record<string, unknown>>;
}

export type ScenarioProgressStep = {
  label: string;
  message?: string;
  status: "pending" | "running" | "done" | "error";
};

export async function selectScenario(sessionId: string, scenario: Record<string, unknown>) {
  const { data } = await api.post(`/sessions/${sessionId}/scenario-selection`, {
    scenario
  });
  return data;
}

export async function fetchSelectedScenario(sessionId: string) {
  const { data } = await api.get(`/sessions/${sessionId}/scenario-selection`);
  return data.scenario as Record<string, unknown> | undefined;
}

export async function fetchScenarioProgress(sessionId: string) {
  const { data } = await api.get(`/sessions/${sessionId}/scenario-progress`);
  return (data.steps ?? []) as ScenarioProgressStep[];
}

export type ScenarioAudioMetadata = {
  status?: "pending" | "running" | "done" | "failed";
  job_id?: string;
  path?: string;
  language?: string;
  sample_rate?: number;
  generated_at?: string;
  text_length?: number;
  error?: string;
  requested_at?: string;
  started_at?: string;
  finished_at?: string;
  tts_provider?: string;
  backgrounds_applied?: number;
  background_tracks_requested?: number;
  voice_only_path?: string;
  background_plan?: Array<{
    background: string;
    start_seconds: number;
    duration_seconds: number;
    note?: string | null;
  }>;
};

export async function fetchScenarioAudio(sessionId: string) {
  try {
    const { data } = await api.get(`/sessions/${sessionId}/scenario-audio`);
    return data as ScenarioAudioMetadata;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function synthesizeScenarioAudio(
  sessionId: string,
  payload?: { text?: string; language?: string }
) {
  const { data } = await api.post(`/sessions/${sessionId}/scenario-audio`, payload ?? {});
  return data as ScenarioAudioMetadata;
}

export type SfxPositionPayload = {
  path: string;
  start_seconds: number;
  duration_seconds: number;
};

export async function remixScenarioAudio(
  sessionId: string,
  gainOverrides?: Record<string, number>,
  sfxPositions?: SfxPositionPayload[],
  ambientStart?: number,
  ambientDuration?: number,
  narrationStart?: number,
  narrationVolume?: number,
  ambientVolume?: number,
  sfxVolume?: number
) {
  const { data } = await api.post(`/sessions/${sessionId}/scenario-audio/remix`, {
    gain_overrides: gainOverrides ?? null,
    sfx_positions: sfxPositions && sfxPositions.length > 0 ? sfxPositions : null,
    ambient_start: ambientStart !== undefined ? ambientStart : null,
    ambient_duration: ambientDuration !== undefined ? ambientDuration : null,
    narration_start: narrationStart !== undefined ? narrationStart : null,
    narration_volume: narrationVolume !== undefined ? narrationVolume : null,
    ambient_volume: ambientVolume !== undefined ? ambientVolume : null,
    sfx_volume: sfxVolume !== undefined ? sfxVolume : null,
  });
  return data as ScenarioAudioMetadata;
}

// Instance-based remix: clips are the authoritative mix and are persisted
// server-side so a reload / project switch restores the exact timeline.
export async function remixWithClips(
  sessionId: string,
  clips: AudioClipInstance[],
  volumes?: { narrationVolume?: number; ambientVolume?: number; sfxVolume?: number }
) {
  const { data } = await api.post(`/sessions/${sessionId}/scenario-audio/remix`, {
    clips,
    narration_volume: volumes?.narrationVolume ?? null,
    ambient_volume: volumes?.ambientVolume ?? null,
    sfx_volume: volumes?.sfxVolume ?? null,
  });
  return data as ScenarioAudioMetadata;
}

export function getBackgroundSoundPreviewUrl(path: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  return `${prefix}/background-sounds/preview?path=${encodeURIComponent(path)}`;
}

export function getScenarioAudioUrl(sessionId: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  return `${base}/sessions/${sessionId}/scenario-audio/file`;
}

export type ScenarioImage = {
  id: string;
  filename: string;
  original_name?: string;
  uploaded_at?: string;
  download_url: string;
};

export async function fetchScenarioImages(sessionId: string) {
  const { data } = await api.get(`/sessions/${sessionId}/scenario-images`);
  return data.images as ScenarioImage[];
}

export async function uploadScenarioImage(sessionId: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/sessions/${sessionId}/scenario-images`, form, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return data.image as ScenarioImage;
}

export async function deleteScenarioImage(sessionId: string, imageId: string) {
  await api.delete(`/sessions/${sessionId}/scenario-images/${imageId}`);
}

export async function reorderScenarioImages(sessionId: string, order: string[]) {
  const { data } = await api.post(`/sessions/${sessionId}/scenario-images/reorder`, { order });
  return data.images as ScenarioImage[];
}

export type SlideshowMetadata = {
  status: string;
  path: string;
  created_at?: string;
  image_count?: number;
};

export async function fetchSlideshow(sessionId: string) {
  try {
    const { data } = await api.get(`/sessions/${sessionId}/slideshow`);
    return data as SlideshowMetadata;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function createSlideshow(sessionId: string) {
  const { data } = await api.post(`/sessions/${sessionId}/slideshow`);
  return data as SlideshowMetadata;
}

export function getScenarioSlideshowUrl(sessionId: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  return `${base}/sessions/${sessionId}/slideshow/file`;
}

export function getProjectAudioFileUrl(projectName: string, fileName: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  return `${base}/projects/${encodeURIComponent(projectName)}/audio-file?file=${encodeURIComponent(fileName)}`;
}

export function getProjectTranscriptionBundleUrl(projectName: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  return `${base}/projects/${encodeURIComponent(projectName)}/transcription-bundle`;
}

export function getProjectKnowledgeGraphViewUrl(projectName: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  return `${base}/projects/${encodeURIComponent(projectName)}/knowledge-graph-view`;
}

export function getProjectFinalAudioUrl(projectName: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  const encoded = encodeURIComponent(projectName);
  return `${base}/projects/${encoded}/final-audio`;
}

export function getScenarioAudioFileUrl(sessionId: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  return `${base}/sessions/${encodeURIComponent(sessionId)}/scenario-audio/file`;
}

export function getProjectFinalVideoUrl(projectName: string) {
  const prefix = (API_BASE_URL || "").replace(/\/$/, "");
  const base = prefix.length > 0 ? prefix : "";
  const encoded = encodeURIComponent(projectName);
  return `${base}/projects/${encoded}/slideshow`;
}

export function getWsBaseUrl() {
  const target = baseURL || window.location.origin;
  // Ensure target has protocol
  let protocol = "ws";
  if (target.startsWith("https")) {
    protocol = "wss";
    return target.replace("https://", "wss://");
  }
  if (target.startsWith("http")) {
    protocol = "ws";
    return target.replace("http://", "ws://");
  }
  // Fallback: if target is just a domain, deduce protocol from window.location
  if (window.location.protocol === "https:") {
    return `wss://${target}`;
  }
  return `ws://${target}`;
}
